import { useMemo } from 'react';
import { useWebSocketConversation } from '@features/Admin/Campaign/hooks/useWebSocketConversation';
import { useMonitoringTranscriptions } from '@shared/hooks/useMonitoringTranscriptions';
import { ExtendedConversationLogDto, ConversationMessagesReturn } from '../types';
import { mergeLiveTranscriptions } from '../liveTranscriptions';

export const useConversationMessages = (
  callId: string,
  isRoomConnected: boolean,
  onError?: <T>(error: T) => void,
): ConversationMessagesReturn => {
  const { initialLoading: loading, conversationData } = useWebSocketConversation(callId, onError);

  const { transcriptions: monitoringTranscriptions, debugInfo } = useMonitoringTranscriptions();

  const allMessages = useMemo(() => {
    const messages = conversationData.data.map((message): ExtendedConversationLogDto => {
      const baseMessage = {
        id: message.id,
        conversationId: message.conversationId,
        actor: message.actor,
        createdAt: message.createdAt,
        updatedAt: message.updatedAt,
      };

      if (message.type === 'tool_call') {
        return { ...baseMessage, type: 'tool_call', toolCall: message.toolCall };
      }

      return {
        ...baseMessage,
        type: 'text',
        message: message.message ?? '',
      };
    });

    const merged = isRoomConnected
      ? mergeLiveTranscriptions(messages, monitoringTranscriptions, callId)
      : messages;

    return merged.sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
  }, [conversationData.data, monitoringTranscriptions, isRoomConnected, callId]);

  return { allMessages, loading, debugInfo };
};
